import React, { useState, useMemo } from "react";
import {
  Paper,
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  Checkbox,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Collapse,
  IconButton,
  Autocomplete,
  TextField,
  Chip,
  Stack,
} from "@mui/material";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { loadJsonData } from "../utils/hybridDataLoader";
import { useDriverCarNumbers } from "../hooks/useDriverCarNumbers";
import { useCurrentSeason } from "../hooks/useCurrentSeason";
import { useDriverManufacturers } from "../hooks/useDriverManufacturers";
import { tableContainerSx } from "../styles/tableStyles";
import ManufacturerLogo from "./ManufacturerLogo";
import { raceMonthShort, getRaceYear } from "../utils/raceDate";

const playoffLabelMap = {
  playoff_16: 'Round 16',
  playoff_12: 'Round 12',
  playoff_8: 'Round 8',
  playoff_4: 'Final',
  chase: 'The Chase'
};

const FantasyAdvancedTab = ({
  groupDrivers,
  trackRaces,
  similarTrackRaces,
  currentSeasonRaces,
  allRaces,
  pastSeasonRaces,
}) => {
  const [trackFilter, setTrackFilter] = useState("similar"); // similar, aero, both
  const [manualMode, setManualMode] = useState(false);
  const [selectedTracks, setSelectedTracks] = useState([]);
  const [yearRange, setYearRange] = useState("last2"); // last2, last3, nextgen, or current
  const [expandedDriver, setExpandedDriver] = useState(null);
  const [calendar, setCalendar] = React.useState([]);
  const [nextRaceAeroPackage, setNextRaceAeroPackage] = React.useState("");
  const [robustMetrics, setRobustMetrics] = useState(false);
  
  const driverCarNumbers = useDriverCarNumbers(allRaces);
  const driverManufacturers = useDriverManufacturers(allRaces);

  // Load calendar data to get aero packages
  React.useEffect(() => {
    const loadCalendar = async () => {
      try {
        const calendarData = await loadJsonData("calendar.json");
        setCalendar(calendarData);

        // Get next race info and find its aero package
        const nextRaceData = await loadJsonData("next_race_data.json");
        const nextRace = calendarData.find(
          r => r.season_year === nextRaceData.next_race_season &&
               r.race_number === nextRaceData.next_race_number
        );
        setNextRaceAeroPackage(nextRace?.aero_package || "");
      } catch (error) {
        console.error("Error loading calendar:", error);
      }
    };
    loadCalendar();
  }, []);

  // The season the data is in (not the calendar year: from 1 January until the first race the new year has no races)
  const currentYear = useCurrentSeason();
  const previousYear = currentYear - 1;

  // Combine all available races (current + past seasons)
  const allAvailableRaces = useMemo(() => {
    return [...currentSeasonRaces, ...pastSeasonRaces];
  }, [currentSeasonRaces, pastSeasonRaces]);

  // Filter races based on year range
  const yearFilteredRaces = useMemo(() => {
    if (yearRange === "current") {
      return allAvailableRaces.filter(race => {
        const raceYear = race.season_year || getRaceYear(race.race_date);
        return raceYear === currentYear;
      });
    }
    if (yearRange === "last3") {
      return allAvailableRaces.filter(race => {
        const raceYear = race.season_year || getRaceYear(race.race_date);
        return raceYear >= currentYear - 2; // Current + past 2 seasons
      });
    }
    if (yearRange === "nextgen") {
      return allAvailableRaces.filter(race => {
        const raceYear = race.season_year || getRaceYear(race.race_date);
        return raceYear >= 2022; // NextGen era starts in 2022
      });
    }
    // last2 years (default)
    return allAvailableRaces.filter(race => {
      const raceYear = race.season_year || getRaceYear(race.race_date);
      return raceYear === currentYear || raceYear === previousYear;
    });
  }, [allAvailableRaces, yearRange, currentYear, previousYear]);

  // Get unique tracks from all races for manual selection
  const availableTracks = useMemo(() => {
    const uniqueTracks = [...new Set(allAvailableRaces.map(r => r.track_name))];
    return uniqueTracks.sort();
  }, [allAvailableRaces]);

  // Filter races based on track filter selection
  const filteredRaces = useMemo(() => {
    if (manualMode) {
      // Manual track selection
      return yearFilteredRaces.filter(race =>
        selectedTracks.includes(race.track_name)
      );
    }

    // Quick filters logic
    if (trackFilter === "similar") {
      // Use similar track races with year filtering applied
      const similarTrackNames = new Set(similarTrackRaces.map(r => r.track_name));
      return yearFilteredRaces.filter(race => similarTrackNames.has(race.track_name));
    }

    if (trackFilter === "aero") {
      // Same aero package races
      return yearFilteredRaces.filter(race => {
        const calendarRace = calendar.find(
          c => c.season_year === race.season_year && c.race_number === race.race_number
        );
        return calendarRace?.aero_package === nextRaceAeroPackage;
      });
    }

    if (trackFilter === "both") {
      // Similar tracks AND same aero package (intersection)
      const similarTrackNames = new Set(similarTrackRaces.map(r => r.track_name));
      return yearFilteredRaces.filter(race => {
        if (!similarTrackNames.has(race.track_name)) return false;
        const calendarRace = calendar.find(
          c => c.season_year === race.season_year && c.race_number === race.race_number
        );
        return calendarRace?.aero_package === nextRaceAeroPackage;
      });
    }

    return yearFilteredRaces;
  }, [manualMode, trackFilter, yearFilteredRaces, similarTrackRaces, selectedTracks, calendar, nextRaceAeroPackage]);

  // Helper function to calculate average (pure or robust)
  const calculateAverage = (values, useRobust = false) => {
    if (values.length === 0) return 0;
    if (values.length <= 2 || !useRobust) {
      // Pure average (or not enough data for robust)
      return values.reduce((sum, val) => sum + val, 0) / values.length;
    }
    // Robust average: remove min and max, then average the rest
    const sorted = [...values].sort((a, b) => a - b);
    const trimmed = sorted.slice(1, -1); // Remove first (min) and last (max)
    return trimmed.reduce((sum, val) => sum + val, 0) / trimmed.length;
  };

  // Calculate statistics for each driver
  const driverStats = useMemo(() => {
    return groupDrivers.map(driver => {
      const driverRaces = filteredRaces.filter(r => r.driver_name === driver);

      if (driverRaces.length === 0) {
        return {
          driver,
          avgFinish: "-",
          avgStart: "-",
          avgRating: "-",
          avgRunningPos: "-",
          top15LapsPct: "-",
          avgStagePoints: "-",
          avgCrashesInvolved: "-",
          avgFailuresInvolved: "-",
          avgGreenFlagPasses: "-",
          avgGreenFlagTimesPassed: "-",
          avgQualityPasses: "-",
          races: []
        };
      }

      const avgFinish = calculateAverage(driverRaces.map(r => r.race_pos), robustMetrics).toFixed(1);
      const avgStart = calculateAverage(driverRaces.map(r => r.quali_pos), robustMetrics).toFixed(1);
      const avgRating = calculateAverage(driverRaces.map(r => r.driver_rating), robustMetrics).toFixed(1);
      const avgRunningPos = calculateAverage(driverRaces.map(r => r.avg_pos || 0), robustMetrics).toFixed(1);
      const top15LapsPct = calculateAverage(driverRaces.map(r => r.pct_top_15_laps || 0), robustMetrics).toFixed(1) + "%";
      const avgStagePoints = calculateAverage(driverRaces.map(r => r.race_stage_points || 0), robustMetrics).toFixed(1);
      const avgCrashesInvolved = calculateAverage(driverRaces.map(r => r.crashes_involved || 0), robustMetrics).toFixed(1);
      const avgFailuresInvolved = calculateAverage(driverRaces.map(r => r.failures_involved || 0), robustMetrics).toFixed(1);
      const avgGreenFlagPasses = calculateAverage(driverRaces.map(r => r.green_flag_passes || 0), robustMetrics).toFixed(1);
      const avgGreenFlagTimesPassed = calculateAverage(driverRaces.map(r => r.green_flag_times_passed || 0), robustMetrics).toFixed(1);
      const avgQualityPasses = calculateAverage(driverRaces.map(r => r.quality_passes || 0), robustMetrics).toFixed(1);
      const avgFantasyPts = calculateAverage(
        driverRaces.map(r => (r.race_finish_points || 0) + (r.race_stage_points || 0)), 
        robustMetrics
      ).toFixed(1);

      // Sort races by date descending (latest first)
      const sortedRaces = driverRaces.sort((a, b) => {
        const dateA = new Date(a.race_date);
        const dateB = new Date(b.race_date);
        return dateB - dateA;
      });

      return {
        driver,
        avgFinish,
        avgStart,
        avgRating,
        avgRunningPos,
        top15LapsPct,
        avgStagePoints,
        avgCrashesInvolved,
        avgFailuresInvolved,
        avgGreenFlagPasses,
        avgGreenFlagTimesPassed,
        avgQualityPasses,
        avgFantasyPts,
        races: sortedRaces
      };
    });
  }, [groupDrivers, filteredRaces, robustMetrics]);

  // Sort drivers by avg finish
  const sortedDriverStats = useMemo(() => {
    return [...driverStats].sort((a, b) => {
      if (a.avgFinish === "-") return 1;
      if (b.avgFinish === "-") return -1;
      return parseFloat(a.avgFinish) - parseFloat(b.avgFinish);
    });
  }, [driverStats]);

  const handleManualModeToggle = () => {
    setManualMode(!manualMode);
    setSelectedTracks([]);
  };

  const handleTrackFilterChange = (event) => {
    setTrackFilter(event.target.value);
  };

  return (
    <Paper sx={{ borderRadius: 3, p: 3, boxShadow: 3 }}>
      {/* Filters Section */}
      <Box sx={{ display: "flex", gap: 3, mb: 3, flexWrap: "wrap" }}>
        {/* Quick Filters */}
        <Box sx={{ flex: 1, minWidth: 300 }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: "#666" }}>
            Quick Filters
          </Typography>
          <Box sx={{
            bgcolor: (theme) => theme.palette.mode === 'dark' ? '#2a2a2a' : '#f9f9f9',
            p: 2,
            borderRadius: 2,
            border: '1px solid',
            borderColor: (theme) => theme.palette.mode === 'dark' ? '#444' : '#e0e0e0',
          }}>
            <RadioGroup value={trackFilter} onChange={handleTrackFilterChange}>
              <FormControlLabel
                value="similar"
                control={<Radio disabled={manualMode} />}
                label="Similar Tracks"
              />
              <FormControlLabel
                value="aero"
                control={<Radio disabled={manualMode} />}
                label={`Same Aero Package${nextRaceAeroPackage ? ` (${nextRaceAeroPackage})` : ''}`}
              />
              <FormControlLabel
                value="both"
                control={<Radio disabled={manualMode} />}
                label="Similar Tracks + Same Aero"
              />
            </RadioGroup>
          </Box>
        </Box>

        {/* Manual Selection */}
        <Box sx={{ flex: 1, minWidth: 300 }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: "#666" }}>
            Manual Selection
          </Typography>
          <Box sx={{
            bgcolor: manualMode
              ? (theme) => theme.palette.mode === 'dark' ? '#3a2f1c' : '#fff8e1'
              : (theme) => theme.palette.mode === 'dark' ? '#1e1e1e' : '#fafafa',
            p: 2,
            borderRadius: 2,
            border: '1px solid',
            borderColor: manualMode
              ? (theme) => theme.palette.mode === 'dark' ? '#7a6a3a' : '#ffd54f'
              : (theme) => theme.palette.mode === 'dark' ? '#333' : '#e0e0e0',
            opacity: manualMode ? 1 : 0.6,
          }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={manualMode}
                  onChange={handleManualModeToggle}
                />
              }
              label={<Typography sx={{ fontWeight: 600 }}>Choose Specific Tracks</Typography>}
            />
            {manualMode && (
              <Autocomplete
                multiple
                options={availableTracks}
                value={selectedTracks}
                onChange={(e, newValue) => setSelectedTracks(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    size="small"
                    placeholder="Select tracks..."
                    sx={{ mt: 1 }}
                  />
                )}
              />
            )}
          </Box>
        </Box>

        {/* Year Range Toggle */}
        <Box sx={{ minWidth: 200 }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: "#666" }}>
            Time Range
          </Typography>
          <Stack direction="column" spacing={1}>
            <Stack direction="row" spacing={1}>
              <Chip
                label={`${currentYear} Only`}
                color={yearRange === "current" ? "secondary" : "default"}
                onClick={() => setYearRange("current")}
                clickable
                size="small"
              />
              <Chip
                label={`${previousYear}-${currentYear}`}
                color={yearRange === "last2" ? "secondary" : "default"}
                onClick={() => setYearRange("last2")}
                clickable
                size="small"
              />
            </Stack>
            <Stack direction="row" spacing={1}>
              <Chip
                label={`${currentYear - 2}-${currentYear}`}
                color={yearRange === "last3" ? "secondary" : "default"}
                onClick={() => setYearRange("last3")}
                clickable
                size="small"
              />
              <Chip
                label="NextGen Era (2022+)"
                color={yearRange === "nextgen" ? "secondary" : "default"}
                onClick={() => setYearRange("nextgen")}
                clickable
                size="small"
              />
            </Stack>
          </Stack>
        </Box>

        {/* Robust Metrics Toggle */}
        <Box sx={{ minWidth: 200 }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: "#666" }}>
            Calculation Method
          </Typography>
          <Stack direction="column" spacing={1}>
            <Chip
              label="Pure Average"
              color={!robustMetrics ? "primary" : "default"}
              onClick={() => setRobustMetrics(false)}
              clickable
              size="medium"
              sx={{ justifyContent: 'flex-start' }}
            />
            <Chip
              label="Robust Average"
              color={robustMetrics ? "primary" : "default"}
              onClick={() => setRobustMetrics(true)}
              clickable
              size="medium"
              sx={{ justifyContent: 'flex-start' }}
            />
          </Stack>
          <Typography variant="caption" sx={{ mt: 1, display: 'block', color: '#999', fontSize: '0.7rem' }}>
            {robustMetrics
              ? "Removes best & worst values"
              : "Includes all values"}
          </Typography>
        </Box>
      </Box>

      {/* Main Table */}
      <TableContainer sx={tableContainerSx}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.200' }}>
              <TableCell sx={{ width: 40 }} />
              <TableCell sx={{ fontWeight: "bold", px: 2, fontSize: "0.85rem" }}>#</TableCell>
              <TableCell sx={{ fontWeight: "bold", px: 2, fontSize: "0.85rem" }}>Driver</TableCell>
              <TableCell sx={{ fontWeight: "bold", textAlign: "center", fontSize: "0.85rem" }}>Finish</TableCell>
              <TableCell sx={{ fontWeight: "bold", textAlign: "center", fontSize: "0.85rem" }}>Start</TableCell>
              <TableCell sx={{ fontWeight: "bold", textAlign: "center", fontSize: "0.85rem" }}>Rating</TableCell>
              <TableCell sx={{ fontWeight: "bold", textAlign: "center", fontSize: "0.85rem" }}>Running Pos</TableCell>
              <TableCell sx={{ fontWeight: "bold", textAlign: "center", fontSize: "0.85rem" }}>Top-15 Laps %</TableCell>
              <TableCell sx={{ fontWeight: "bold", textAlign: "center", fontSize: "0.85rem" }}>Stage Pts</TableCell>
              <TableCell sx={{ fontWeight: "bold", textAlign: "center", fontSize: "0.85rem" }}>Fantasy Pts</TableCell>
              <TableCell sx={{ fontWeight: "bold", textAlign: "center", fontSize: "0.85rem" }}>Crashes</TableCell>
              {/* <TableCell sx={{ fontWeight: "bold", textAlign: "center", fontSize: "0.85rem" }}>Failures</TableCell> */}
              <TableCell sx={{ fontWeight: "bold", textAlign: "center", fontSize: "0.85rem" }}>Passes</TableCell>
              <TableCell sx={{ fontWeight: "bold", textAlign: "center", fontSize: "0.85rem" }}>Times Passed</TableCell>
              <TableCell sx={{ fontWeight: "bold", textAlign: "center", fontSize: "0.85rem" }}>Quality Passes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedDriverStats.map((stats, index) => (
              <React.Fragment key={stats.driver}>
                <TableRow
                  sx={{
                    bgcolor: index % 2 === 0 ? "background.default" : "action.hover",
                    '& > *': { borderBottom: 'unset' },
                    cursor: 'pointer',
                    "&:hover": { bgcolor: "action.selected" },
                  }}
                  onClick={() => setExpandedDriver(expandedDriver === stats.driver ? null : stats.driver)}
                >
                  <TableCell sx={{ width: 40 }}>
                    <IconButton size="small">
                      {expandedDriver === stats.driver ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", px: 2, fontSize: "0.85rem" }}>
                    {driverCarNumbers[stats.driver] || "-"}
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", px: 2, fontSize: "0.85rem" }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <ManufacturerLogo manufacturer={driverManufacturers[stats.driver]} />
                      {stats.driver}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ textAlign: "center", fontSize: "0.85rem" }}>{stats.avgFinish}</TableCell>
                  <TableCell sx={{ textAlign: "center", fontSize: "0.85rem" }}>{stats.avgStart}</TableCell>
                  <TableCell sx={{ textAlign: "center", fontSize: "0.85rem" }}>{stats.avgRating}</TableCell>
                  <TableCell sx={{ textAlign: "center", fontSize: "0.85rem" }}>{stats.avgRunningPos}</TableCell>
                  <TableCell sx={{ textAlign: "center", fontSize: "0.85rem" }}>{stats.top15LapsPct}</TableCell>
                  <TableCell sx={{ textAlign: "center", fontSize: "0.85rem" }}>{stats.avgStagePoints}</TableCell>
                  <TableCell sx={{ textAlign: "center", fontSize: "0.85rem" }}>{stats.avgFantasyPts}</TableCell>
                  <TableCell sx={{ textAlign: "center", fontSize: "0.85rem" }}>{stats.avgCrashesInvolved}</TableCell>
                  {/* <TableCell sx={{ textAlign: "center", fontSize: "0.85rem" }}>{stats.avgFailuresInvolved}</TableCell> */}
                  <TableCell sx={{ textAlign: "center", fontSize: "0.85rem" }}>{stats.avgGreenFlagPasses}</TableCell>
                  <TableCell sx={{ textAlign: "center", fontSize: "0.85rem" }}>{stats.avgGreenFlagTimesPassed}</TableCell>
                  <TableCell sx={{ textAlign: "center", fontSize: "0.85rem" }}>{stats.avgQualityPasses}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={13} sx={{ p: 0 }}>
                    <Collapse in={expandedDriver === stats.driver} timeout="auto" unmountOnExit>
                      <Box sx={{ margin: 2 }}>
                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                          Race Details ({stats.races.length} races)
                        </Typography>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ bgcolor: (theme) => theme.palette.mode === 'dark' ? '#1e1e1e' : '#f5f5f5' }}>
                              <TableCell sx={{ fontWeight: "bold", fontSize: "0.85rem" }}>Date</TableCell>
                              <TableCell sx={{ fontWeight: "bold", fontSize: "0.85rem", textAlign: "center" }}>Finish</TableCell>
                              <TableCell sx={{ fontWeight: "bold", fontSize: "0.85rem", textAlign: "center" }}>Start</TableCell>
                              <TableCell sx={{ fontWeight: "bold", fontSize: "0.85rem", textAlign: "center" }}>Rating</TableCell>
                              <TableCell sx={{ fontWeight: "bold", fontSize: "0.85rem", textAlign: "center" }}>Avg Pos</TableCell>
                              <TableCell sx={{ fontWeight: "bold", fontSize: "0.85rem", textAlign: "center" }}>Top-15 %</TableCell>
                              <TableCell sx={{ fontWeight: "bold", fontSize: "0.85rem", textAlign: "center" }}>Stage Pts</TableCell>
                              <TableCell sx={{ fontWeight: "bold", fontSize: "0.85rem", textAlign: "center" }}>Fantasy Pts</TableCell>
                              <TableCell sx={{ fontWeight: "bold", fontSize: "0.85rem", textAlign: "center" }}>Crashes</TableCell>
                              {/* <TableCell sx={{ fontWeight: "bold", fontSize: "0.85rem", textAlign: "center" }}>Failures</TableCell> */}
                              <TableCell sx={{ fontWeight: "bold", fontSize: "0.85rem", textAlign: "center" }}>Passes</TableCell>
                              <TableCell sx={{ fontWeight: "bold", fontSize: "0.85rem", textAlign: "center" }}>Times Passed</TableCell>
                              <TableCell sx={{ fontWeight: "bold", fontSize: "0.85rem", textAlign: "center" }}>Quality Passes</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {stats.races.map((race, idx) => {
                              const calendarRace = calendar.find(
                                c => c.season_year === race.season_year && c.race_number === race.race_number
                              );
                              const monthLabel = raceMonthShort(race.race_date);
                              const year = race.season_year || getRaceYear(race.race_date);
                              const playoffNote = calendarRace?.stage && calendarRace.stage !== 'season'
                                ? playoffLabelMap[calendarRace.stage]
                                : null;
                              const isDnf = race.status !== 'finished';

                              return (
                                <TableRow
                                  key={idx}
                                  sx={{
                                    bgcolor: isDnf
                                      ? (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 0, 0, 0.15)' : 'rgba(255, 0, 0, 0.05)'
                                      : playoffNote
                                      ? (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 215, 0, 0.15)' : 'rgba(255, 215, 0, 0.1)'
                                      : idx % 2 === 0
                                      ? (theme) => theme.palette.mode === 'dark' ? '#1e1e1e' : '#fff'
                                      : (theme) => theme.palette.mode === 'dark' ? '#2a2a2a' : '#f9f9f9'
                                  }}
                                >
                                  <TableCell sx={{ fontSize: "0.85rem" }}>
                                    {monthLabel} {year} – {race.track_short_name || race.track_name}
                                    {playoffNote && (
                                      <Box component="span" sx={{
                                        ml: 1,
                                        px: 0.75,
                                        py: 0.25,
                                        borderRadius: 0.5,
                                        fontSize: '0.7rem',
                                        fontWeight: 600,
                                        bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 215, 0, 0.2)' : '#fff9c4',
                                        color: (theme) => theme.palette.mode === 'dark' ? '#ffa726' : '#f57f17',
                                      }}>
                                        {playoffNote}
                                      </Box>
                                    )}
                                    {isDnf && (
                                      <Box component="span" sx={{
                                        ml: 1,
                                        px: 0.75,
                                        py: 0.25,
                                        borderRadius: 0.5,
                                        fontSize: '0.7rem',
                                        fontWeight: 600,
                                        bgcolor: '#ffcdd2',
                                        color: '#c62828',
                                      }}>
                                        DNF
                                      </Box>
                                    )}
                                  </TableCell>
                                  <TableCell sx={{ fontSize: "0.85rem", textAlign: "center" }}>{race.race_pos}</TableCell>
                                  <TableCell sx={{ fontSize: "0.85rem", textAlign: "center" }}>{race.quali_pos}</TableCell>
                                  <TableCell sx={{ fontSize: "0.85rem", textAlign: "center" }}>{race.driver_rating?.toFixed(1) || "-"}</TableCell>
                                  <TableCell sx={{ fontSize: "0.85rem", textAlign: "center" }}>{race.avg_pos?.toFixed(1) || "-"}</TableCell>
                                  <TableCell sx={{ fontSize: "0.85rem", textAlign: "center" }}>{race.pct_top_15_laps ? race.pct_top_15_laps.toFixed(1) + "%" : "-"}</TableCell>
                                  <TableCell sx={{ fontSize: "0.85rem", textAlign: "center" }}>{race.race_stage_points || "-"}</TableCell>
                                  <TableCell sx={{ fontSize: "0.85rem", textAlign: "center" }}>{(race.race_finish_points || 0) + (race.race_stage_points || 0)}</TableCell>
                                  <TableCell sx={{ fontSize: "0.85rem", textAlign: "center" }}>{race.crashes_involved || 0}</TableCell>
                                  {/* <TableCell sx={{ fontSize: "0.85rem", textAlign: "center" }}>{race.failures_involved || 0}</TableCell> */}
                                  <TableCell sx={{ fontSize: "0.85rem", textAlign: "center" }}>{race.green_flag_passes || 0}</TableCell>
                                  <TableCell sx={{ fontSize: "0.85rem", textAlign: "center" }}>{race.green_flag_times_passed || 0}</TableCell>
                                  <TableCell sx={{ fontSize: "0.85rem", textAlign: "center" }}>{race.quality_passes || 0}</TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default FantasyAdvancedTab;
